import {
	CheckCircle, Close, Download, FilterAlt, PeopleAlt, TrendingUp, Visibility, Warning
} from "@mui/icons-material";
import { Button, IconButton, MenuItem, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import EpiStatCard from "../../components/epi/EpiStatCard";
import { EpiCountBadge, EpiStatusChip } from "../../components/epi/EpiStatusChip";
import { EPI_COLORS, companyLabel, complianceColors, complianceLabels } from "../../components/epi/epiColors";
import { EpiComplianceService } from "../../services/EpiComplianceService";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiComplianceRowDTO, EpiComplianceSummaryDTO } from "../../types/EpiComplianceDTO";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";
import { EpiPdfService, loadCompanyLogo } from "../../utils/EpiPdfService";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiCompliance.scss';

dayjs.extend(customParseFormat);

const pageSizes = [25, 50, 100];

const EpiCompliance: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [companies, setCompanies] = useState<EpiCompanyConfigDTO[]>([]);
	const [companyId, setCompanyId] = useState<number | ''>('');
	const [summary, setSummary] = useState<EpiComplianceSummaryDTO>();
	const [sector, setSector] = useState('');
	const [position, setPosition] = useState('');
	const [status, setStatus] = useState('');
	const [search, setSearch] = useState('');
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [fromValue, setFromValue] = useState<Dayjs | null>(null);
	const [toValue, setToValue] = useState<Dayjs | null>(null);
	const [fromError, setFromError] = useState(false);
	const [toError, setToError] = useState(false);
	const [page, setPage] = useState(0);
	const [size, setSize] = useState(pageSizes[0]);
	const [loading, setLoading] = useState(false);
	const requestSeq = useRef(0);
	const lastAppliedFrom = useRef('');
	const lastAppliedTo = useRef('');
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const service = new EpiComplianceService();
	const configService = new EpiInspectionService();
	const pdfService = new EpiPdfService();

	useEffect(() => {
		const loadCompanies = async () => {
			const response = await configService.getCompanies();
			const enabled = (response?.data || []).filter(company => company.enabled);
			setCompanies(enabled);
			if (enabled.length === 1) {
				setCompanyId(enabled[0].companyId);
			}
		};
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EPI_COMPLIANCE');
		}
	}, []);

	useEffect(() => {
		search_();
	}, [companyId]);

	const rows = summary?.rows || [];
	const filtered = filter(rows);
	const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / size) - 1));
	const visible = filtered.slice(currentPage * size, currentPage * size + size);
	const sectors = distinct(rows.map(row => row.sector));
	const positions = distinct(rows.map(row => row.position));

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-compliance">
				<div className="cabecalho">
					<Typography variant="h6">Gestão da Conformidade NR-06</Typography>
					<span>
						Acompanhe a situação de conformidade dos trabalhadores e o histórico
						de inspeções de EPIs.
					</span>
				</div>
				<div className="filtros">
					<TextField
						select
						label="Empresa"
						size="small"
						value={companyId}
						onChange={(e) => setCompanyId(Number(e.target.value))}
					>
						{companies.map(company => (
							<MenuItem key={company.companyId} value={company.companyId}>
								{companyLabel(company)}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Setor"
						size="small"
						value={sector}
						onChange={(e) => { setSector(e.target.value); setPage(0); }}
					>
						<MenuItem value="">Todos os setores</MenuItem>
						{sectors.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Cargo"
						size="small"
						value={position}
						onChange={(e) => { setPosition(e.target.value); setPage(0); }}
					>
						<MenuItem value="">Todos os cargos</MenuItem>
						{positions.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
					</TextField>
					<TextField
						label="Colaborador / Matrícula"
						size="small"
						value={search}
						onChange={(e) => { setSearch(e.target.value); setPage(0); }}
					/>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Situação"
						size="small"
						value={status}
						onChange={(e) => { setStatus(e.target.value); setPage(0); }}
					>
						<MenuItem value="">Todos</MenuItem>
						<MenuItem value="CONFORME">Conforme</MenuItem>
						<MenuItem value="NAO_CONFORME">Não conforme</MenuItem>
						<MenuItem value="SEM_INSPECAO">Sem inspeção no período</MenuItem>
					</TextField>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
						<DatePicker
							label="De"
							value={fromValue}
							onChange={(newValue) => handleFromChange(newValue)}
							slotProps={{ textField: { size: 'small', error: fromError, helperText: fromError ? 'Data inválida' : undefined } }}
						/>
						<DatePicker
							label="Até"
							value={toValue}
							onChange={(newValue) => handleToChange(newValue)}
							slotProps={{ textField: { size: 'small', error: toError, helperText: toError ? 'Data inválida' : undefined } }}
						/>
					</LocalizationProvider>
					<div className="acoes-filtro">
						<Button variant="outlined" startIcon={<FilterAlt />} onClick={clearFilters}>
							Limpar filtros
						</Button>
					</div>
				</div>
				{loading
					? getLoading()
					: !summary
						? <div className="sem-execucoes">
							<Typography variant="h6">
								Selecione uma empresa para ver a conformidade.
							</Typography>
						</div>
						: <Fragment>
							<div className="indicadores">
								<EpiStatCard
									variant="label-first"
									icon={<PeopleAlt />}
									color={EPI_COLORS.info}
									label="Colaboradores monitorados"
									value={String(summary.monitoredEmployees)}
									detail="100% do total"
								/>
								<EpiStatCard
									variant="label-first"
									icon={<CheckCircle />}
									color={EPI_COLORS.success}
									label="Conformes"
									value={String(summary.compliantEmployees)}
									detail={percentOf(summary.compliantEmployees, summary.monitoredEmployees)}
								/>
								<EpiStatCard
									variant="label-first"
									icon={<Warning />}
									color={EPI_COLORS.warning}
									label="Não conformes"
									value={String(summary.nonCompliantEmployees)}
									detail={percentOf(summary.nonCompliantEmployees, summary.monitoredEmployees)}
								/>
								<EpiStatCard
									variant="label-first"
									icon={<Close />}
									color={EPI_COLORS.danger}
									label="Sem inspeção no período"
									value={String(summary.uninspectedEmployees)}
									detail={percentOf(summary.uninspectedEmployees, summary.monitoredEmployees)}
								/>
								<EpiStatCard
									variant="label-first"
									icon={<TrendingUp />}
									color={EPI_COLORS.purple}
									label="Índice de Conformidade NR-06"
									value={formatIndex(summary.complianceIndex)}
									detail={`Meta: ${summary.complianceGoal}%`}
									chipLabel={summary.goalReached ? 'Meta atingida' : undefined}
									chipColor={EPI_COLORS.success}
								/>
							</div>
							<div className="resultados">
								<span>
									Resultados encontrados: {filtered.length} · período {summary.periodFrom} a {summary.periodTo}
								</span>
								{permissions.includes('EXPORT_EPI_REPORT') && (
									<Button
										variant="outlined"
										startIcon={<Download />}
										onClick={async () => pdfService.exportComplianceToPdf(summary, filtered, await loadCompanyLogo(summary.companyId))}
									>
										Exportar relatório
									</Button>
								)}
							</div>
							<div className="tabela">
								<div className="header-tabela">
									<p>Colaborador</p>
									<p>Matrícula</p>
									<p>Cargo</p>
									<p>Setor</p>
									<p>Índice NR-06</p>
									<p>Última inspeção</p>
									<p>NC abertas</p>
									<p>Ações em andamento</p>
									<p>Exames pendentes</p>
									<p>Situação</p>
									<p>Ação</p>
								</div>
								<div className="body-table">
									{visible.map(row => (
										<div className="linha-empresa" key={row.employeeId}>
											<p
												className="colaborador"
												title={row.employeeName || '—'}
												style={{ color: complianceColors[row.status] || EPI_COLORS.neutral }}
											>
												{row.employeeName || '—'}
											</p>
											<p className="matricula">{formatRegistration(row.registration)}</p>
											<p title={row.position || '—'}>{row.position || '—'}</p>
											<p title={row.sector || '—'}>{row.sector || '—'}</p>
											<p style={{ color: indexColor(row.complianceIndex, summary.complianceGoal) }}>
												{formatIndex(row.complianceIndex)}
											</p>
											<p>{row.lastInspectionDate || '—'}</p>
											<p>
												<EpiCountBadge
													value={row.openNonConformities}
													color={row.openNonConformities > 0
														? EPI_COLORS.danger
														: EPI_COLORS.success}
												/>
											</p>
											<p>
												<EpiCountBadge
													value={row.ongoingActions}
													color={row.ongoingActions > 0
														? EPI_COLORS.warning
														: EPI_COLORS.success}
												/>
											</p>
											<p>
												<EpiCountBadge
													value={row.pendingExams}
													color={row.pendingExams > 0
														? EPI_COLORS.warning
														: EPI_COLORS.success}
												/>
											</p>
											<p>
												<EpiStatusChip
													label={complianceLabels[row.status] || row.status}
													color={complianceColors[row.status] || EPI_COLORS.neutral}
												/>
											</p>
											<p className="acao">
												{row.lastInspectionId && permissions.includes('EPI_INSPECTION')
													? <IconButton
														size="small"
														onClick={() => navigate(`/inspecao-epis/inspecoes/${row.lastInspectionId}`)}
													>
														<Visibility fontSize="small" />
													</IconButton>
													: '—'}
											</p>
										</div>
									))}
								</div>
							</div>
							<div className="paginacao">
								<TablePagination
									component="div"
									rowsPerPageOptions={pageSizes}
									count={filtered.length}
									rowsPerPage={size}
									page={currentPage}
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
									labelRowsPerPage="Registros por página"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
								/>
							</div>
						</Fragment>
				}
			</div>
		</Fragment>
	);

	async function search_(overrides?: { from?: string, to?: string }) {
		if (companyId === '') {
			setSummary(undefined);
			return;
		}
		const requestedFrom = overrides?.from ?? from;
		const requestedTo = overrides?.to ?? to;
		const seq = ++requestSeq.current;
		setLoading(true);
		const response = await service.getCompliance(Number(companyId), requestedFrom, requestedTo);
		if (seq !== requestSeq.current) {
			return;
		}
		setSummary(response?.data);
		setPage(0);
		setLoading(false);
	}

	function clearFilters() {
		setSector('');
		setPosition('');
		setStatus('');
		setSearch('');
		const hadPeriod = !!from || !!to;
		setFrom('');
		setTo('');
		setFromValue(null);
		setToValue(null);
		setFromError(false);
		setToError(false);
		lastAppliedFrom.current = '';
		lastAppliedTo.current = '';
		setPage(0);
		if (hadPeriod) {
			search_({ from: '', to: '' });
		}
	}

	function handleFromChange(newValue: Dayjs | null) {
		setFromValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setFromError(true);
			return;
		}
		setFromError(false);
		const value = newValue === null ? '' : newValue.format('DD/MM/YYYY');
		if (value === lastAppliedFrom.current) {
			return;
		}
		lastAppliedFrom.current = value;
		setFrom(value);
		search_({ from: value });
	}

	function handleToChange(newValue: Dayjs | null) {
		setToValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setToError(true);
			return;
		}
		setToError(false);
		const value = newValue === null ? '' : newValue.format('DD/MM/YYYY');
		if (value === lastAppliedTo.current) {
			return;
		}
		lastAppliedTo.current = value;
		setTo(value);
		search_({ to: value });
	}


	function filter(all: EpiComplianceRowDTO[]) {
		return all.filter(row => {
			if (sector && row.sector !== sector) {
				return false;
			}
			if (position && row.position !== position) {
				return false;
			}
			if (status && row.status !== status) {
				return false;
			}
			if (!search) {
				return true;
			}
			return (row.employeeName || '').toUpperCase().includes(search.toUpperCase())
				|| (row.registration || '').includes(search);
		});
	}

	function distinct(values: string[]) {
		return Array.from(new Set(values.filter(value => !!value))).sort();
	}

	function percentOf(value: number, total: number) {
		return total ? `${(value * 100 / total).toFixed(1)}% do total` : '—';
	}

	function formatRegistration(registration?: string) {
		if (!registration) {
			return '—';
		}
		if (registration.length <= 10) {
			return registration;
		}
		return (
			<Tooltip title={registration}>
				<span>{`${registration.slice(0, 10)}...`}</span>
			</Tooltip>
		);
	}

	function formatIndex(value?: number) {
		return value === undefined || value === null ? '—' : `${value.toFixed(1)}%`;
	}

	function indexColor(value: number | undefined, goal: number) {
		if (value === undefined || value === null) {
			return undefined;
		}
		if (value >= goal) {
			return EPI_COLORS.success;
		}
		return value >= 70 ? EPI_COLORS.warning : EPI_COLORS.danger;
	}
};

export default EpiCompliance;
